import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export interface PurchaseSavedItem {
  id: string;
  name: string | null;
  image_url: string | null;
  store: string | null;
  price: number | null;
  currency: string | null;
}

export interface Purchase {
  id: string;
  saved_item_id: string;
  user_id: string;
  purchased_by: string;
  purchase_price: number | null;
  currency: string;
  purchased_at: string;
  notes: string | null;
  created_at: string;
  saved_item?: PurchaseSavedItem | null;
}

export function usePurchases() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: purchases = [], isLoading, error } = useQuery({
    queryKey: ['purchases', user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      const { data, error } = await supabase
        .from('purchases')
        .select(`
          *,
          saved_item:saved_items(
            id, page_id, name, image_url, store, price, currency,
            flux_pages:page_id(url, tld, data)
          )
        `)
        .eq('purchased_by', user.id)
        .order('purchased_at', { ascending: false });

      if (error) throw error;

      // Resolve product fields from flux_pages → metadata → saved_items
      return (data || []).map(row => {
        const si = row.saved_item;
        if (!si) return { ...row, saved_item: null } as unknown as Purchase;

        const fp = si.flux_pages;
        const d = fp?.data as Record<string, any> | null;
        const hasFluxData = si.page_id && d && typeof d === 'object' && !Array.isArray(d);

        const resolved: PurchaseSavedItem = {
          id: si.id,
          name: hasFluxData ? (d.title || si.name || null) : (si.name ?? null),
          image_url: hasFluxData
            ? (d.cdnUrls?.[0] || d.imageUrls?.[0] || si.image_url || null)
            : (si.image_url ?? null),
          store: hasFluxData ? (fp!.tld || d.brand || si.store || null) : (si.store ?? null),
          price: hasFluxData
            ? (d.price != null ? parseFloat(String(d.price)) : (si.price ?? null))
            : (si.price ?? null),
          currency: hasFluxData ? (d.currency || si.currency || 'USD') : (si.currency ?? 'USD'),
        };

        return { ...row, saved_item: resolved } as unknown as Purchase;
      });
    },
    enabled: !!user,
    // Keep previous data during refetches to prevent flicker
    placeholderData: (previousData) => previousData ?? [],
    // Optimize remounting: use cached data while refreshing in background
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
    refetchOnMount: false, // Don't refetch if data exists
    structuralSharing: true,
  });

  const markAsPurchased = useMutation({
    mutationFn: async ({ 
      savedItemId, 
      purchasePrice, 
      currency,
      itemOwnerId 
    }: { 
      savedItemId: string; 
      purchasePrice?: number | null;
      currency?: string;
      itemOwnerId: string;
    }) => {
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('purchases')
        .insert({
          saved_item_id: savedItemId,
          user_id: itemOwnerId,
          purchased_by: user.id,
          purchase_price: purchasePrice ?? null,
          currency: currency || 'GBP',
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchases', user?.id] });
    },
  });

  const markMultipleAsPurchased = useMutation({
    mutationFn: async (items: { savedItemId: string; purchasePrice?: number | null; currency?: string; itemOwnerId: string }[]) => {
      if (!user) throw new Error('Not authenticated');
      if (items.length === 0) return;

      const insertData = items.map(item => ({
        saved_item_id: item.savedItemId,
        user_id: item.itemOwnerId,
        purchased_by: user.id,
        purchase_price: item.purchasePrice ?? null,
        currency: item.currency || 'GBP',
      }));

      const { error } = await supabase
        .from('purchases')
        .insert(insertData);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchases', user?.id] });
    },
  });

  const removePurchase = useMutation({
    mutationFn: async (purchaseId: string) => {
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('purchases')
        .delete()
        .eq('id', purchaseId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchases', user?.id] });
    },
  });

  // Check if a specific item is purchased
  const isItemPurchased = (savedItemId: string) => {
    return purchases.some(p => p.saved_item_id === savedItemId);
  };

  // Get purchase record for an item
  const getPurchaseForItem = (savedItemId: string) => {
    return purchases.find(p => p.saved_item_id === savedItemId);
  };

  // Calculate total spent
  const totalSpent = purchases.reduce((sum, p) => {
    return sum + (p.purchase_price || 0);
  }, 0);

  // Get purchases this month
  const thisMonthPurchases = purchases.filter(p => {
    const purchaseDate = new Date(p.purchased_at);
    const now = new Date();
    return purchaseDate.getMonth() === now.getMonth() && 
           purchaseDate.getFullYear() === now.getFullYear();
  });

  const thisMonthSpent = thisMonthPurchases.reduce((sum, p) => {
    return sum + (p.purchase_price || 0);
  }, 0);

  return {
    purchases,
    isLoading,
    error,
    markAsPurchased,
    markMultipleAsPurchased,
    removePurchase,
    isItemPurchased,
    getPurchaseForItem,
    totalSpent,
    thisMonthSpent,
    thisMonthCount: thisMonthPurchases.length,
  };
}
